// Единые форматтеры и предикаты дат для всего приложения.
// До этого каждая фича писала свои: имя собиралось в 11 местах, деньги — в 5
// вариантах, даты — в 3. Любой новый экран должен брать отсюда.

import { i18n } from '../../i18n/index.js';

/** BCP47 tag for Intl APIs, derived from the active interface locale. */
function intlLocale(): string {
  return i18n.locale === 'en' ? 'en-US' : 'ru-RU';
}

/** Полное имя человека из частей; пустые части отбрасываются. */
export function formatPersonName(
  firstName?: string | null,
  lastName?: string | null,
  fallback = '—',
): string {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  return name || fallback;
}

/** Денежная сумма в рублях (или другой валюте) без дробной части. */
export function formatMoney(
  amount: number | string | null | undefined,
  currency = 'RUB',
  currencyDisplay: 'symbol' | 'code' = 'symbol',
): string {
  if (amount === null || amount === undefined) return '—';
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(intlLocale(), {
    style: 'currency',
    currency,
    currencyDisplay,
    maximumFractionDigits: 0,
  }).format(value);
}

/** Дата и время в локали пользователя; пустое значение → «—». */
export function formatDateTime(
  iso?: string | Date | null,
  options: { timeZone?: string; readable?: boolean } = {},
): string {
  if (!iso) return '—';
  const date = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(intlLocale(), {
    day: options.readable && i18n.locale === 'en' ? 'numeric' : '2-digit',
    month: options.readable && i18n.locale === 'en' ? 'short' : '2-digit',
    year: 'numeric',
    hour: options.readable && i18n.locale === 'en' ? 'numeric' : '2-digit',
    minute: '2-digit',
    ...(options.timeZone ? { timeZone: options.timeZone } : {}),
  });
}

/** Только дата, без времени. */
export function formatDate(
  iso?: string | Date | null,
  options: { timeZone?: string; readable?: boolean } = {},
): string {
  if (!iso) return '—';
  const date = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(intlLocale(), {
    day: options.readable && i18n.locale === 'en' ? 'numeric' : '2-digit',
    month: options.readable && i18n.locale === 'en' ? 'short' : '2-digit',
    year: 'numeric',
    ...(options.timeZone ? { timeZone: options.timeZone } : {}),
  });
}

/** Дата в прошлом (для подсветки просроченного). */
export function isPastDue(iso?: string | Date | null): boolean {
  if (!iso) return false;
  const time = (iso instanceof Date ? iso : new Date(iso)).getTime();
  return Number.isFinite(time) && time < Date.now();
}

/** ISO → значение для <input type="datetime-local"> (локальное время, без секунд). */
export function isoToLocalInput(iso?: string | Date | null): string {
  if (!iso) return '';
  const date = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Значение datetime-local → ISO для API (или undefined, если пусто/некорректно). */
export function localInputToIso(local?: string | null): string | undefined {
  if (!local) return undefined;
  const date = new Date(local);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

/** «Сейчас» в формате datetime-local — для атрибута min. */
export function nowLocalInput(): string {
  return isoToLocalInput(new Date());
}
